import {
  BetType,
  OrderSingleRaceDetail,
  Race,
  RaceProgram,
} from '@/lib/apollo/types/gen/graphql';
import {
  BetDataList,
  BetMethod,
  BettableList,
  FirstThreeRacerList,
  RacerNumber,
} from '@/modelTypes';
import { raceSelector } from '@/selectors/race';
import { toHalfWidth } from '@/utility/utils';

import { dayjst } from '../dayjst';
import { PurchaseProductDetail } from '../dmm/trackingTag/type';
import { Campaign } from '../microcms/model';
import { getVelodromeByCode } from './velodrome';

export const getBetTypeName = (betType: BetType) => {
  switch (betType) {
    case BetType.Trifecta: {
      return '3連単';
    }
    case BetType.Trio: {
      return '3連複';
    }
    case BetType.Perfecta: {
      return '2車単';
    }
    case BetType.Quinella: {
      return '2車複';
    }
    case BetType.BracketPerfecta: {
      return '2枠単';
    }
    case BetType.BracketQuinella: {
      return '2枠複';
    }
    case BetType.QuinellaPlace: {
      return 'ワイド';
    }
    default: {
      return '';
    }
  }
};

export const getBetTypeEnum = (betTypeName: string) => {
  switch (betTypeName) {
    case '3連単': {
      return BetType.Trifecta;
    }
    case '3連複': {
      return BetType.Trio;
    }
    case '2車単': {
      return BetType.Perfecta;
    }
    case '2車複': {
      return BetType.Quinella;
    }
    case '2枠単': {
      return BetType.BracketPerfecta;
    }
    case '2枠複': {
      return BetType.BracketQuinella;
    }
    case 'ワイド': {
      return BetType.QuinellaPlace;
    }
    default: {
      return null;
    }
  }
};

export const getBetMethodName = (betMethod: BetMethod) => {
  switch (betMethod) {
    case BetMethod.Formation: {
      return 'フォーメーション';
    }
    case BetMethod.Box: {
      return 'BOX';
    }
    case BetMethod.Wheel: {
      return '流し';
    }
    case BetMethod.Odds: {
      return 'オッズ';
    }
    default: {
      return '';
    }
  }
};

/**
 * ある配列を基に順列を生成し返す関数
 */
export const makePermutation = <T>(inputArr: T[]): T[][] => {
  const result: T[][] = [];

  const permute = (arr: T[], m: T[] = []) => {
    if (arr.length === 0) {
      result.push(m);
    } else {
      arr.forEach((_, i) => {
        const curr = arr.slice();
        const next = curr.splice(i, 1);
        permute(curr.slice(), m.concat(next));
      });
    }
  };

  permute(inputArr);

  return result;
};

/**
 * ある配列を基に組み合わせを生成し返す関数
 */
export const makeCombination = <T>(inputArr: T[], size: number): T[][] => {
  const result = [] as T[][];

  if (inputArr.length < size) {
    return [];
  }

  inputArr.forEach((arr, i) => {
    if (size === 1) {
      result[i] = [arr];
    } else {
      const row = makeCombination(inputArr.slice(i + 1), size - 1);
      row.forEach(rowArr => {
        result.push([arr].concat(rowArr));
      });
    }
  });

  return result;
};

export const makeBettableList = (
  isMulti: boolean,
  first: RacerNumber[],
  second: RacerNumber[],
  third: RacerNumber[],
): BettableList => {
  let setNumArray: number[][] = [];
  let setRacerNumsArray: RacerNumber[][] = [];

  first.forEach(fn => {
    second.forEach(sn => {
      if (fn.bicycleNum !== sn.bicycleNum) {
        setRacerNumsArray.push([fn, sn]);
      }
      third.forEach(tn => {
        if (
          fn.bicycleNum !== sn.bicycleNum &&
          sn.bicycleNum !== tn.bicycleNum &&
          fn.bicycleNum !== tn.bicycleNum
        ) {
          setNumArray.push([fn.bicycleNum, sn.bicycleNum, tn.bicycleNum]);
        }
      });
    });
  });

  if (isMulti) {
    setNumArray = setNumArray.reduce(
      (acc, cur) => acc.concat(makePermutation(cur)),
      [] as number[][],
    );
    setRacerNumsArray = setRacerNumsArray.reduce(
      (acc, cur) => acc.concat(makePermutation(cur)),
      [] as RacerNumber[][],
    );
  }

  const sortedSetNumArray = (array: number[]) => {
    return Array.from(new Set(array))
      .sort()
      .map(num => ({
        setNum: num,
        isRemoved: false,
      }));
  };

  const trifecta = sortedSetNumArray(
    setNumArray.map(array => Number(array.join(''))),
  );
  const trio = sortedSetNumArray(
    setNumArray.map(array => Number(array.sort().join(''))),
  );
  const perfecta = sortedSetNumArray(
    setRacerNumsArray.map(array =>
      Number(array.map(racerNums => racerNums.bicycleNum).join('')),
    ),
  );
  const quinella = sortedSetNumArray(
    setRacerNumsArray.map(array =>
      Number(
        array
          .map(racerNums => racerNums.bicycleNum)
          .sort()
          .join(''),
      ),
    ),
  );
  const bracketPerfecta = sortedSetNumArray(
    setRacerNumsArray.map(array =>
      Number(array.map(racerNums => racerNums.bracketNum).join('')),
    ),
  );
  const bracketQuinella = sortedSetNumArray(
    setRacerNumsArray.map(array =>
      Number(
        array
          .map(racerNums => racerNums.bracketNum)
          .sort()
          .join(''),
      ),
    ),
  );

  return {
    trifecta,
    trio,
    perfecta,
    quinella,
    bracketPerfecta,
    bracketQuinella,
    quinellaPlace: quinella,
  };
};

const shortedRaceEventNameMap: {
  [key: string]: { oneWord: string; twoWords: string };
} = {
  '予　選': { oneWord: '予', twoWords: '予選' },
  準決勝: { oneWord: '準', twoWords: '準決' },
  準々決: { oneWord: '準', twoWords: '準々' },
  '決　勝': { oneWord: '決', twoWords: '決勝' },
  '一　般': { oneWord: '般', twoWords: '一般' },
  '選　抜': { oneWord: '抜', twoWords: '選抜' },
  '特　選': { oneWord: '特', twoWords: '特選' },
  '特　秀': { oneWord: '秀', twoWords: '特秀' },
  敗復活: { oneWord: '復', twoWords: '復活' },
  順位決: { oneWord: '順', twoWords: '順位' },
  一予選: { oneWord: '一', twoWords: '一予' },
  二予選: { oneWord: '二', twoWords: '二予' },
  初特選: { oneWord: '初', twoWords: '初特' },
  '優　勝': { oneWord: '優', twoWords: '優勝' },
  東一予: { oneWord: '一', twoWords: '東一' },
  西一予: { oneWord: '一', twoWords: '西一' },
  東二予: { oneWord: '二', twoWords: '東二' },
  西二予: { oneWord: '二', twoWords: '西二' },
  特選予: { oneWord: '予', twoWords: '特予' },
  青龍賞: { oneWord: '青', twoWords: '青龍' },
  白虎賞: { oneWord: '白', twoWords: '白虎' },
  ＤＲＭ: { oneWord: 'ド', twoWords: 'ＤＲ' },
  選抜一: { oneWord: '抜', twoWords: '抜一' },
  選抜二: { oneWord: '抜', twoWords: '抜二' },
  ＯＲＩ: { oneWord: 'オ', twoWords: 'ＯＲ' },
  特選一: { oneWord: '特', twoWords: '特一' },
  東特選: { oneWord: '特', twoWords: '東特' },
  西特選: { oneWord: '特', twoWords: '西特' },
  特選二: { oneWord: '特', twoWords: '特二' },
  ＤＭＤ: { oneWord: 'ダ', twoWords: 'ＤＭ' },
  ＳＴＲ: { oneWord: 'ス', twoWords: 'ＳＲ' },
  ＧＤＲ: { oneWord: 'ゴ', twoWords: 'ＧＲ' },
  一般一: { oneWord: '般', twoWords: '般一' },
  一般二: { oneWord: '般', twoWords: '般二' },
  特一般: { oneWord: '般', twoWords: '特般' },
  東王座: { oneWord: '準', twoWords: '東王' },
  西王座: { oneWord: '準', twoWords: '西王' },
  新予選: { oneWord: '予', twoWords: '新予' },
  新人特: { oneWord: '特', twoWords: '新特' },
  新選抜: { oneWord: '抜', twoWords: '新抜' },
  新一般: { oneWord: '般', twoWords: '新般' },
  新準決: { oneWord: '準', twoWords: '新準' },
  新決勝: { oneWord: '決', twoWords: '新決' },
  国予選: { oneWord: '予', twoWords: '国予' },
  国選抜: { oneWord: '抜', twoWords: '国抜' },
  国決勝: { oneWord: '決', twoWords: '国決' },
  'Ｇ　Ｐ': { oneWord: 'Ｇ', twoWords: 'ＧＰ' },
  'Ｎ　Ｃ': { oneWord: 'Ｎ', twoWords: 'ＮＣ' },
  ふる賞: { oneWord: 'ふ', twoWords: 'ふ賞' },
  ＳＰＲ: { oneWord: 'Ｐ', twoWords: 'ＰＲ' },
  ＷＧＰ: { oneWord: 'Ｇ', twoWords: 'ＷＧ' },
  国準Ａ: { oneWord: 'Ａ', twoWords: '国Ａ' },
  国準Ｂ: { oneWord: 'Ｂ', twoWords: '国Ｂ' },
  国順位: { oneWord: '順', twoWords: '国順' },
  ローズ: { oneWord: 'ロ', twoWords: 'ロー' },
  二予Ａ: { oneWord: 'Ａ', twoWords: '二Ａ' },
  二予Ｂ: { oneWord: 'Ｂ', twoWords: '二Ｂ' },
  日競杯: { oneWord: '理', twoWords: '日杯' },
  'Ｄ　Ｓ': { oneWord: 'Ｄ', twoWords: 'ＤＳ' },
  'Ｗ　Ｓ': { oneWord: 'Ｗ', twoWords: 'ＷＳ' },
  ＹＧＰ: { oneWord: 'Ｙ', twoWords: 'ＹＧ' },
  共同賞: { oneWord: '共', twoWords: '共賞' },
  予ＴＲ: { oneWord: '予', twoWords: '予Ｔ' },
  特ＴＲ: { oneWord: '特', twoWords: '特Ｔ' },
  新人王: { oneWord: '決', twoWords: '新決' },
  国際Ｒ: { oneWord: '国', twoWords: '国Ｒ' },
  '東　賞': { oneWord: '東', twoWords: '東賞' },
  '西　賞': { oneWord: '西', twoWords: '西賞' },
  準決Ａ: { oneWord: 'Ａ', twoWords: '準Ａ' },
  準決Ｂ: { oneWord: 'Ｂ', twoWords: '準Ｂ' },
  準決Ｃ: { oneWord: 'Ｃ', twoWords: '準Ｃ' },
  二予Ｃ: { oneWord: 'Ｃ', twoWords: '二Ｃ' },
  順位Ａ: { oneWord: 'Ａ', twoWords: '順Ａ' },
  順位Ｂ: { oneWord: 'Ｂ', twoWords: '順Ｂ' },
  '優　秀': { oneWord: '優', twoWords: '優秀' },
  特選Ａ: { oneWord: 'Ａ', twoWords: '特Ａ' },
  特選Ｂ: { oneWord: 'Ｂ', twoWords: '特Ｂ' },
  敗復一: { oneWord: '復', twoWords: '復一' },
  敗復二: { oneWord: '復', twoWords: '復二' },
  選抜Ａ: { oneWord: 'Ａ', twoWords: '抜Ａ' },
  選抜Ｂ: { oneWord: 'Ｂ', twoWords: '抜Ｂ' },
  ＷＳＲ: { oneWord: 'Ｓ', twoWords: 'ＷＳ' },
  予選１: { oneWord: '予', twoWords: '予１' },
  予選２: { oneWord: '予', twoWords: '予２' },
  東予選: { oneWord: '予', twoWords: '東予' },
  西予選: { oneWord: '予', twoWords: '西予' },
  東決勝: { oneWord: '決', twoWords: '東決' },
  西決勝: { oneWord: '決', twoWords: '西決' },
  チ予選: { oneWord: '予', twoWords: 'チ予' },
  チ準決: { oneWord: '準', twoWords: 'チ準' },
  チ一般: { oneWord: '般', twoWords: 'チ般' },
  チ決勝: { oneWord: '決', twoWords: 'チ決' },
  チ選抜: { oneWord: '抜', twoWords: 'チ抜' },
  特予選: { oneWord: '予', twoWords: '特予' },
  チ一予: { oneWord: '一', twoWords: 'チ一' },
  チ二予: { oneWord: '二', twoWords: 'チ二' },
  順位１: { oneWord: '順', twoWords: '順１' },
  順位２: { oneWord: '順', twoWords: '順２' },
  順位３: { oneWord: '順', twoWords: '順３' },
  順位４: { oneWord: '順', twoWords: '順４' },
  順位５: { oneWord: '順', twoWords: '順５' },
  シャイ: { oneWord: 'シ', twoWords: 'シャ' },
  Ａ級Ｆ: { oneWord: 'Ｆ', twoWords: 'ＡＦ' },
  'チ・Ｆ': { oneWord: 'Ｆ', twoWords: 'チＦ' },
  '支援　': { oneWord: '援', twoWords: '支援' },
  東予１: { oneWord: '予', twoWords: '予１' },
  西予１: { oneWord: '予', twoWords: '予１' },
  東予２: { oneWord: '予', twoWords: '予２' },
  西予２: { oneWord: '予', twoWords: '予２' },
  みのり: { oneWord: 'み', twoWords: 'みの' },
  ガ予１: { oneWord: '予', twoWords: '予１' },
  ガ予２: { oneWord: '予', twoWords: '予２' },
  ガ決勝: { oneWord: '決', twoWords: 'ガ決' },
  ガ一般: { oneWord: '般', twoWords: 'ガ般' },
  ＧＧＰ: { oneWord: 'ガ', twoWords: 'ガＧ' },
  Ｇコレ: { oneWord: 'ガ', twoWords: 'ガＣ' },
  ガ予選: { oneWord: '予', twoWords: 'ガ予' },
  エボリ: { oneWord: 'エ', twoWords: 'エボ' },
  ガ選抜: { oneWord: '抜', twoWords: 'ガ抜' },
};

export const oneWordRaceEventNameMapper = (raceEventName: string) => {
  return shortedRaceEventNameMap[raceEventName]
    ? shortedRaceEventNameMap[raceEventName].oneWord
    : raceEventName.slice(0, 1);
};

export const twoWordsRaceEventNameMapper = (raceEventName: string) => {
  return shortedRaceEventNameMap[raceEventName]
    ? shortedRaceEventNameMap[raceEventName].twoWords
    : raceEventName.slice(0, 2);
};

// マルチで買える賭式か判定する（3連単・2車単・2枠単はマルチ可能）
export const isPossibleMulti = (betType: BetType) => {
  return [BetType.Trifecta, BetType.Perfecta, BetType.BracketPerfecta].includes(
    betType,
  );
};

// 買い目データから欠車車番が含まれているものを削除する
export const filterInvalidBetData = (
  raceData: Race,
  prevBetData: BetDataList,
  notReleasedEntryRacers: number[],
): BetDataList => {
  const temp = { ...prevBetData };
  const targetData = temp[raceData.id];
  const checkList: boolean[] = [];
  Object.entries(targetData).forEach(([key, value]) => {
    const beforeTicketsLength = value.tickets.length;
    const availableTickets = value.tickets.filter(ticket =>
      notReleasedEntryRacers.every(
        num => !ticket.setNum.toString().includes(num.toString()),
      ),
    );
    if (availableTickets.length) {
      targetData[key].tickets = availableTickets;

      const filterdFirstThreeRacer = value.firstThreeRacerList.map(items =>
        items.filter(item =>
          notReleasedEntryRacers.every(num => item.bicycleNum !== num),
        ),
      ) as FirstThreeRacerList;
      targetData[key].firstThreeRacerList = filterdFirstThreeRacer;
    } else {
      delete targetData[key];
    }
    checkList.push(availableTickets.length === beforeTicketsLength);
  });

  // 対象レースの買い目が全て削除になったとき対象レースの買い目データは削除
  if (!Object.keys(targetData).length) {
    delete temp[raceData.id];
    return temp;
  }

  // 欠車車番を含む買い目があったときは対象を削除して更新
  if (checkList.some(x => !x)) {
    return temp;
  }

  // 買い目に変更がなかったときは元データを返却
  return prevBetData;
};

// 投票締切が近い順にソートする
export const orderByOrderDeadline = (rps: RaceProgram[]) => {
  const finishedPrograms = rps.filter(
    rp => rp.isCanceled || rp.races.every(r => !raceSelector(r).isHeld),
  );

  const orderedList = rps
    .filter(rp => !finishedPrograms.includes(rp))
    .sort((a, b) => {
      const raceA = a.races.find(r => raceSelector(r).isHeld);
      const raceB = b.races.find(r => raceSelector(r).isHeld);
      if (raceA && raceB) {
        return raceA.orderDeadline.diff(raceB.orderDeadline);
      }
      return 0;
    });

  return [...orderedList, ...finishedPrograms];
};

// 販売中の開催のみを抽出する
export const filterSalePrograms = (rps: RaceProgram[]) =>
  rps.filter(rp =>
    rp.races.find(
      r => !rp.isCanceled && !rp.isSalesSuspended && raceSelector(r).isHeld,
    ),
  );

export const formatRaceInfoText = (
  className: Race['raceClassName'],
  eventName: Race['raceEventName'],
) => {
  return toHalfWidth(`${className}${eventName.replace(/\s+/g, '')}`);
};

export const formatPurchaseProductDetail = (
  velodromeCode: number,
  raceNum: number,
  details: OrderSingleRaceDetail[],
): PurchaseProductDetail[] => {
  return details.map(detail => ({
    product: {
      id: detail.orderSingleRaceId,
      item_name:
        `${getBetTypeName(detail.betType)}` +
        `_${getVelodromeByCode(velodromeCode).name}` +
        `_${raceNum}R` +
        `_[${detail.numbers.join('-')}]`,
      item_variant:
        `${detail.betType}` +
        `_vc${velodromeCode}` +
        `_r${raceNum}` +
        `_${detail.numbers.join('')}`,
      priceInfo: { price: detail.amount / detail.count },
    },
    quantity: detail.count,
  }));
};

/**
 * 開催名がない場合は`{競輪場名}競輪 {グレード}`を返す
 * i.g. 函館競輪 F2
 */
export const formatProgramName = (raceProgram: RaceProgram) =>
  toHalfWidth(
    `${
      raceProgram.programName ||
      `${getVelodromeByCode(raceProgram.velodromeCode).name}競輪 ${
        raceProgram.gradeName
      }`
    }`,
  );

// 開催に紐づいたキャンペーンを返却する
export const findCampaign = (
  rp: RaceProgram,
  campaigns: Campaign[],
): Campaign | undefined => {
  return campaigns.find(c => {
    if (c.raceProgramInfo) {
      // まず開催日の一致チェック
      if (
        c.raceProgramInfo.targetDates.some(d =>
          dayjst(d.date).isSame(rp.heldDate, 'day'),
        )
      ) {
        // romanName -> 「nagoya / matsudo」のような物理名が格納されている
        const romanName = getVelodromeByCode(rp.velodromeCode).romanName;

        // 「velodrome」で一致していれば「true」を返却
        if (c.raceProgramInfo.velodrome?.id === romanName) {
          return true;
        }

        // 「subVelodromes」内部で一致していれば「true」を返却
        return c.raceProgramInfo.subVelodromes.some(
          sub => sub.id === romanName,
        );
      }
    }
    return false;
  });
};
